import Phaser from 'phaser';
import { GameScene } from './GameScene.js';
import { ArchiveReplayScene } from './ArchiveReplayScene.js';
import { readGameCanvasColor } from '../ui/theme.js';

export const PHASER_BASE_WIDTH = 1920;
export const PHASER_BASE_HEIGHT = 1080;

const SCALE_NONE = Phaser.Scale.ScaleModes.NONE;
const SCALE_FIT = Phaser.Scale.ScaleModes.FIT;
const CENTER_BOTH = Phaser.Scale.Center.CENTER_BOTH;
const CENTER_NONE = Phaser.Scale.Center.NO_CENTER;

function getInitialCanvasBg(): string {
  if (typeof document !== 'undefined') {
    return readGameCanvasColor();
  }
  return '#1e293b';
}

let phaserGame: Phaser.Game | null = null;
let playScaleMode: number = SCALE_FIT;
let playAutoCenter: number = CENTER_BOTH;

/** Instance Phaser unique (Jeu + replay archives) — évite un 2ᵉ contexte WebGL vide. */
export function getPhaserGame(): Phaser.Game {
  if (phaserGame) return phaserGame;

  phaserGame = new Phaser.Game({
    type: Phaser.AUTO,
    width: PHASER_BASE_WIDTH,
    height: PHASER_BASE_HEIGHT,
    parent: 'game-container',
    backgroundColor: getInitialCanvasBg(),
    scale: {
      mode: SCALE_FIT,
      autoCenter: CENTER_BOTH,
      width: PHASER_BASE_WIDTH,
      height: PHASER_BASE_HEIGHT,
    },
    antialias: true,
    physics: {
      default: 'arcade',
      arcade: {
        gravity: { x: 0, y: 0 },
        debug: false,
      },
    },
    scene: [GameScene, ArchiveReplayScene],
  });

  playScaleMode = phaserGame.scale.scaleMode;
  playAutoCenter = phaserGame.scale.autoCenter;

  return phaserGame;
}

/** Taille utile du conteneur replay (zone sous la barre d’outils). */
export function readArchiveMountSize(mount: HTMLElement): { w: number; h: number } {
  const w = Math.max(320, mount.clientWidth || mount.parentElement?.clientWidth || 640);
  const h = Math.max(280, mount.clientHeight || Math.round(w * 0.52));
  return { w, h };
}

function applyArchiveCanvasStyles(canvas: HTMLCanvasElement, w: number, h: number): void {
  canvas.style.display = 'block';
  canvas.style.margin = '0';
  canvas.style.padding = '0';
  canvas.style.position = 'relative';
  canvas.style.left = '0';
  canvas.style.top = '0';
  canvas.style.width = `${w}px`;
  canvas.style.height = `${h}px`;
  canvas.style.maxWidth = '100%';
}

function clearCanvasInlineStyles(canvas: HTMLCanvasElement): void {
  canvas.style.width = '';
  canvas.style.height = '';
  canvas.style.margin = '';
  canvas.style.padding = '';
  canvas.style.position = '';
  canvas.style.left = '';
  canvas.style.top = '';
  canvas.style.maxWidth = '';
  canvas.style.display = '';
}

/**
 * Déplace le canvas dans la zone archives et dimensionne Phaser sur ce conteneur.
 * Phaser 3.90 n’a pas setMode() : on utilise scaleMode + resize (mode NONE).
 */
export function attachPhaserCanvasToArchiveMount(mount: HTMLElement): { w: number; h: number } {
  const game = phaserGame;
  if (!game?.canvas) return { w: 640, h: 333 };
  const { w, h } = readArchiveMountSize(mount);

  if (game.canvas.parentElement !== mount) {
    mount.appendChild(game.canvas);
  }

  game.scale.autoCenter = CENTER_NONE;
  game.scale.scaleMode = SCALE_NONE;
  game.scale.setParentSize(w, h);
  game.scale.resize(w, h);
  applyArchiveCanvasStyles(game.canvas, w, h);

  return { w, h };
}

export function resizeArchivePhaserCanvas(mount: HTMLElement): { w: number; h: number } {
  const game = phaserGame;
  if (!game?.canvas) return readArchiveMountSize(mount);
  const { w, h } = readArchiveMountSize(mount);
  game.scale.setParentSize(w, h);
  game.scale.resize(w, h);
  applyArchiveCanvasStyles(game.canvas, w, h);
  return { w, h };
}

export function restorePhaserCanvasToPlay(): void {
  const game = phaserGame;
  const playContainer = document.getElementById('game-container');
  if (!game?.canvas || !playContainer) return;
  if (game.canvas.parentElement !== playContainer) {
    playContainer.appendChild(game.canvas);
  }

  game.scale.autoCenter = playAutoCenter;
  game.scale.scaleMode = playScaleMode;
  game.scale.resize(PHASER_BASE_WIDTH, PHASER_BASE_HEIGHT);
  game.scale.getParentBounds();
  game.scale.refresh();
  clearCanvasInlineStyles(game.canvas);
}

/** Remet le canvas sur « Jouer » et réactive GameScene après un replay archives. */
export function ensurePhaserReadyForPlay(): void {
  const game = phaserGame;
  if (!game) return;
  if (game.scene.isActive('ArchiveReplayScene')) {
    game.scene.stop('ArchiveReplayScene');
  }
  restorePhaserCanvasToPlay();
  if (game.scene.isPaused('GameScene')) {
    game.scene.resume('GameScene');
  }
}
